/**
 * Base URL for `/api/*` calls. Empty string = same origin (default Next player).
 * Set for the standalone widget when the bundle runs on a third-party page.
 */
let configuredBase = '';

export function configurePlayerApiBase(base: string): void {
  configuredBase = base.replace(/\/+$/, '');
}

export function getPlayerApiBase(): string {
  return configuredBase;
}

/**
 * Asset urls are stored in the DB as root-relative `/uploads/...`. When the player
 * runs as the standalone widget under a sub-path (e.g. embedded at
 * `/projects/mh-ai/c3d`), those urls must be prefixed with the configured API base
 * so they resolve to the proxied uploads endpoint rather than the host page root.
 * When `configuredBase` is empty (default Next player served at origin root) this
 * is a no-op.
 */
export function resolveAssetUrl(url: string): string {
  if (!configuredBase) return url;
  if (typeof url === 'string' && url.startsWith('/uploads/')) {
    return configuredBase + url;
  }
  return url;
}

/**
 * Recursively rewrite every `/uploads/...` string found in a parsed API payload
 * so embedded model / texture / environment urls point at the proxied base.
 */
export function prefixUploadsDeep<T>(value: T): T {
  if (!configuredBase || value == null) return value;
  if (typeof value === 'string') {
    return resolveAssetUrl(value) as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map((v) => prefixUploadsDeep(v)) as unknown as T;
  }
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = prefixUploadsDeep(v);
    }
    return out as unknown as T;
  }
  return value;
}
